import { ChainId } from '@yetiswap/sdk';
import React from 'react';
import { useActiveWeb3React } from '../../../../hooks';
import NFTComboBox, { IComboBoxItem } from '../../components/NFTCombobox';
import { NFTSortingList } from '../../data/NFTFilterOptions';
import { SaleTokenList, ISaleTokenList } from '../../data/SaleTokenList';
import useNFTCollectionList from '../../data/useNFTCollectionList';
import { ActivityFilter, ActivityFilterItem, ActivityFilterItemLabel, ActivityFilterItemOption, ActivityList, ActivityListLoader, ActivityListWrapper, ActivitySidebar, ActivitySidebarHeader, ActivitySummary, ActivitySummaryItem, ActivitySummaryItemLabel, ActivitySummaryItemValue } from './styled';
import AvaxLogo from '../../../../assets/images/avalanche_token_round.png'
import YTSLogo from '../../../../assets/logo/token-logo.png'
import { getAccountHistory, getAllActivities, getStatistics } from '../../../../services/NFTMarketPlace/Graphquery';
import { useGetNFTPrice } from '../../../../hooks/useNFTMarketPlace';
import { YTS } from '../../../../constants';
import debounce from 'lodash/debounce';
import Loader from '../../../../components/Loader';
import { INFTHistory } from '../../models/INFTHistory';
import { MARKET_PLACE } from '../../data/NFTMarketPerPages';
import NFTActivityFormater from '../../nftComponents/Activities';
import Scrollbar from '../../components/Scrollbar';
import Collapse from '../Collapse';

interface IActivityStatistic {
    saleCountWithAVAX: string
    saleCountWithYTS: string
    totalSaleAmountWithAVAX: string
    totalSaleAmountWithYTS: string
}

interface NFTActivityListProps {
    account?: string;
    noStatistic?: boolean;
}

const NFTActivityList = (props: NFTActivityListProps) => {
    const yts = YTS[ChainId.BOBA]

    const [statisticData, setStatisticData] = React.useState<IActivityStatistic | null>(null);

    const [historyType, setHistoryType] = React.useState<string>('All');
    const [nftCurrency, setNFTCurrency] = React.useState<string>('ALL');
    const [nftCollection, setNFTCollection] = React.useState<string>('ALL');
    const [nftSort, setNFTSort] = React.useState<string>('NewestItems');
    const [skipItems, setSkipItems] = React.useState<number>(0);
    const [hasMore, setHasMore] = React.useState<boolean>(true);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [firstLoad, setFirstLoad] = React.useState<boolean>(true);

    const [activitiesData, setActivitiesData] = React.useState<INFTHistory[]>([]);

    const nftCollections = useNFTCollectionList();
    const { chainId } = useActiveWeb3React()

    React.useEffect(() => {
        if (!props.noStatistic) {
            getStatistics().then((res: IActivityStatistic) => setStatisticData(res))
        }
    }, []);

    const statisticAvaxAmount = useGetNFTPrice(statisticData?.totalSaleAmountWithAVAX, yts, true)
    const statisticYtsAmount = useGetNFTPrice(statisticData?.totalSaleAmountWithYTS, yts, true)

    const getAllActivitiesData = React.useCallback(() => {
        setIsLoading(true);
        (props.account ? getAccountHistory(props.account, historyType, skipItems, nftSort, nftCurrency, nftCollection) : getAllActivities(historyType, skipItems, nftSort, nftCurrency, nftCollection)).then(
            (response: INFTHistory[] | null) => {
                if (response) {
                    if (skipItems === 0) {
                        setActivitiesData(response)
                        setFirstLoad(false);
                    } else {
                        setActivitiesData(activitiesData.concat(response));
                    }
                    setHasMore(response.length === MARKET_PLACE.ACTIVITY);
                    setIsLoading(false);
                }
            }
        ).catch(() => {
            setIsLoading(false);
        });
    }, [skipItems, historyType, nftSort, nftCurrency, nftCollection, props.account])

    const onScroll = debounce((position: any) => {
        const isBottom = position && !firstLoad && (position.clientHeight + position.scrollTop >= position.scrollHeight - 200);
        if (isBottom && hasMore && !isLoading) {
            const newSkipItems = skipItems + MARKET_PLACE.ACTIVITY
            setSkipItems(newSkipItems);
        }
    }, 200);

    React.useEffect(() => {
        getAllActivitiesData()
    }, [skipItems, historyType, nftSort, nftCurrency, nftCollection, props.account])

    const activityTypes = React.useMemo(() => {
        return [
            { key: 'All', text: 'All' },
            { key: 'Listed', text: 'Listing' },
            { key: 'Buy', text: 'Purchases' },
            { key: 'Removed', text: 'Removed from Listing' }
        ]
    }, []);

    const collectionFilterItems = React.useMemo(() => {
        let items = nftCollections.map((item: any) => {
            return {
                key: item.tokenAddress.toString(),
                text: item.name,
            }
        });
        items.unshift({ key: 'ALL', text: 'All' });
        return items;
    }, [nftCollections]);

    const currencyList = React.useMemo(() => {
        let items = SaleTokenList.map((item: ISaleTokenList) => {
            return {
                key: item[chainId ? chainId : ChainId.BOBA],
                text: item.name,
                renderItem: () => {
                    return (
                        <span style={{ display: 'flex', alignItems: 'center', width: 120 }}>
                            <img src={item.image} alt={item.name} width={16} height={16} style={{ marginRight: 5 }} />
                            <div>{item.name}</div>
                        </span>
                    )
                }
            } as IComboBoxItem
        });
        items.unshift({ key: 'ALL', text: 'All' });
        return items;
    }, [chainId]);

    const sortFilterItems = React.useMemo(() => {
        return NFTSortingList.map(i => ({ key: i.key, text: i.text }));
    }, []);

    const onChangeActivityType = React.useCallback((item: any) => {
        setHistoryType(item.key);
        setSkipItems(0);
    }, []);

    const onChangeCollection = React.useCallback((item: any) => {
        setNFTCollection(item.key);
        setSkipItems(0);
    }, []);

    const onChangeCurrency = React.useCallback((item: any) => {
        setNFTCurrency(item.key);
        setSkipItems(0);
    }, []);

    const onChangeSort = React.useCallback((item: any) => {
        setNFTSort(item.key);
        setSkipItems(0);
    }, []);

    return (
        <ActivityListWrapper>
            <ActivitySidebar>
                {statisticData &&
                    <>
                        <Collapse title={<ActivitySidebarHeader>Total Statistics</ActivitySidebarHeader>} defaultOpen>
                            <ActivitySummary>
                                <ActivitySummaryItem>
                                    <ActivitySummaryItemLabel>Sales With AVAX</ActivitySummaryItemLabel>
                                    <ActivitySummaryItemValue>{statisticData.saleCountWithAVAX} <img src={AvaxLogo} /></ActivitySummaryItemValue>
                                </ActivitySummaryItem>
                                <ActivitySummaryItem>
                                    <ActivitySummaryItemLabel>Traded With AVAX</ActivitySummaryItemLabel>
                                    <ActivitySummaryItemValue>{statisticAvaxAmount} <img src={AvaxLogo} /></ActivitySummaryItemValue>
                                </ActivitySummaryItem>
                                <ActivitySummaryItem>
                                    <ActivitySummaryItemLabel>Sales With YTS</ActivitySummaryItemLabel>
                                    <ActivitySummaryItemValue>{statisticData.saleCountWithYTS} <img src={YTSLogo} /></ActivitySummaryItemValue>
                                </ActivitySummaryItem>
                                <ActivitySummaryItem>
                                    <ActivitySummaryItemLabel>Traded With YTS</ActivitySummaryItemLabel>
                                    <ActivitySummaryItemValue>{statisticYtsAmount} <img src={YTSLogo} /></ActivitySummaryItemValue>
                                </ActivitySummaryItem>
                                <ActivitySummaryItem>
                                    <ActivitySummaryItemLabel>Total Sales</ActivitySummaryItemLabel>
                                    <ActivitySummaryItemValue>{parseInt(statisticData.saleCountWithYTS) + parseInt(statisticData.saleCountWithAVAX)}</ActivitySummaryItemValue>
                                </ActivitySummaryItem>
                            </ActivitySummary>
                        </Collapse>
                    </>
                }
                <Collapse title={<ActivitySidebarHeader>Filter</ActivitySidebarHeader>} defaultOpen>
                    <ActivityFilter>
                        <ActivityFilterItem>
                            <ActivityFilterItemLabel>Activity Type</ActivityFilterItemLabel>
                            <ActivityFilterItemOption>
                                <NFTComboBox
                                    items={activityTypes}
                                    defaultKey='All'
                                    onSelectedChange={onChangeActivityType}
                                />
                            </ActivityFilterItemOption>
                        </ActivityFilterItem>
                        <ActivityFilterItem>
                            <ActivityFilterItemLabel>Collection</ActivityFilterItemLabel>
                            <ActivityFilterItemOption>
                                <NFTComboBox
                                    items={collectionFilterItems}
                                    defaultKey={'ALL'}
                                    onSelectedChange={onChangeCollection}
                                    allowSearch
                                />
                            </ActivityFilterItemOption>
                        </ActivityFilterItem>
                        <ActivityFilterItem>
                            <ActivityFilterItemLabel>Currency</ActivityFilterItemLabel>
                            <ActivityFilterItemOption>
                                <NFTComboBox
                                    items={currencyList}
                                    defaultKey={"ALL"}
                                    onSelectedChange={onChangeCurrency}
                                />
                            </ActivityFilterItemOption>
                        </ActivityFilterItem>
                        <ActivityFilterItem>
                            <ActivityFilterItemLabel>Sort</ActivityFilterItemLabel>
                            <ActivityFilterItemOption>
                                <NFTComboBox
                                    items={sortFilterItems}
                                    defaultKey={'NewestItems'}
                                    onSelectedChange={onChangeSort}
                                />
                            </ActivityFilterItemOption>
                        </ActivityFilterItem>
                    </ActivityFilter>
                </Collapse>
            </ActivitySidebar>
            <ActivityList>
                <ActivitySidebarHeader>Activities</ActivitySidebarHeader>
                <Scrollbar onUpdate={onScroll}>
                    {activitiesData.map((item: any, index: any) => {
                        //return renderItem(item, index);
                        return <NFTActivityFormater key={`${item.id}_HISTORY_ITEM_${index}`} historyItem={item} chainId={chainId} />
                    })}
                    {isLoading && <ActivityListLoader><Loader size='50px' /></ActivityListLoader>}
                </Scrollbar>
            </ActivityList>
        </ActivityListWrapper>
    )
}


export default NFTActivityList;
